import { useState, useEffect, useCallback, useRef } from "react"
import { Plus, Trash2 } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Separator } from "@/components/ui/separator"
import { toast } from "sonner"
import { getCsrfToken } from "../utils"
import type { Collection } from "../types"

interface CollectionsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  editUrl: string
  destroyUrl: string
  collectionsUrl: string
  bookmarkId: number
  onRemoveBookmark: (bookmarkCount: number) => void
}

export function CollectionsDialog({
  open,
  onOpenChange,
  editUrl,
  destroyUrl,
  collectionsUrl,
  bookmarkId,
  onRemoveBookmark,
}: CollectionsDialogProps) {
  const [collections, setCollections] = useState<Collection[]>([])
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [showNewForm, setShowNewForm] = useState(false)
  const [newName, setNewName] = useState("")
  const [isCreating, setIsCreating] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)
  const listRef = useRef<HTMLDivElement>(null)

  const updateScrollIndicators = useCallback(() => {
    const el = listRef.current
    if (!el) return
    setCanScrollUp(el.scrollTop > 0)
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1)
  }, [])

  const fetchCollections = useCallback(async () => {
    setIsLoading(true)
    try {
      const res = await fetch(editUrl, {
        headers: { Accept: "application/json" },
      })
      if (res.ok) {
        const data = await res.json()
        setCollections(data.collections)
        setSelectedIds(data.selected_collection_ids)
      }
    } catch {
      // Silently fail
    } finally {
      setIsLoading(false)
    }
  }, [editUrl])

  useEffect(() => {
    if (open) {
      fetchCollections()
      setShowNewForm(false)
      setNewName("")
    }
  }, [open, fetchCollections])

  useEffect(() => {
    updateScrollIndicators()
  }, [collections, updateScrollIndicators])

  function toggleCollection(id: number) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  async function handleSave() {
    setIsSaving(true)
    try {
      const res = await fetch(destroyUrl, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": getCsrfToken(),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          bookmark: { collection_ids: selectedIds },
        }),
      })
      if (res.ok) {
        toast.success("Collections updated.")
        onOpenChange(false)
      }
    } catch {
      toast.error("Failed to update collections.")
    } finally {
      setIsSaving(false)
    }
  }

  async function handleRemoveBookmark() {
    setIsRemoving(true)
    try {
      const res = await fetch(destroyUrl, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": getCsrfToken(),
          Accept: "application/json",
        },
      })
      if (!res.ok) throw new Error("Failed")
      const data = await res.json()
      toast.success("Bookmark has been removed.")
      onOpenChange(false)
      onRemoveBookmark(data.bookmark_count)
    } catch {
      toast.error("Failed to remove bookmark.")
    } finally {
      setIsRemoving(false)
    }
  }

  async function handleCreateCollection(e: React.FormEvent) {
    e.preventDefault()
    if (!newName.trim()) return

    setIsCreating(true)
    try {
      const res = await fetch(collectionsUrl, {
        method: "POST",
        headers: {
          "X-CSRF-Token": getCsrfToken(),
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          collection: { name: newName.trim() },
          bookmark_id: bookmarkId,
        }),
      })

      if (res.ok) {
        const data = await res.json()
        setCollections((prev) => [...prev, data])
        setSelectedIds((prev) => [...prev, data.id])
        setNewName("")
        setShowNewForm(false)

        requestAnimationFrame(() => {
          listRef.current?.scrollTo({
            top: listRef.current.scrollHeight,
            behavior: "smooth",
          })
        })
      }
    } catch {
      toast.error("Failed to create collection.")
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Collections</DialogTitle>
          <DialogDescription>
            Manage collections for this bookmark.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-foreground-muted">
            Loading...
          </div>
        ) : (
          <div className="space-y-3">
            {collections.length === 0 && !showNewForm ? (
              <p className="py-4 text-center text-sm text-foreground-muted">
                You don't have any collections yet.
              </p>
            ) : (
              <div className="relative">
                {canScrollUp && (
                  <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-6 rounded-t-md bg-gradient-to-b from-white to-transparent" />
                )}
                <div
                  ref={listRef}
                  onScroll={updateScrollIndicators}
                  className="overflow-y-auto rounded-md border"
                  style={{ maxHeight: "224px" }}
                >
                  {collections.map((collection, index) => {
                    const isSelected = selectedIds.includes(collection.id)
                    return (
                      <label
                        key={collection.id}
                        className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                          isSelected
                            ? "bg-surface-subtle"
                            : "hover:bg-surface-subtle/50"
                        } ${index > 0 ? "border-t border-border-subtle" : ""}`}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() =>
                            toggleCollection(collection.id)
                          }
                        />
                        <span className="text-sm select-none">
                          {collection.name}
                        </span>
                      </label>
                    )
                  })}
                </div>
                {canScrollDown && (
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-6 rounded-b-md bg-gradient-to-t from-white to-transparent" />
                )}
              </div>
            )}

            {showNewForm ? (
              <form onSubmit={handleCreateCollection} className="flex gap-2">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Collection name"
                  autoFocus
                  className="flex-1"
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={isCreating || !newName.trim()}
                >
                  {isCreating ? "..." : "Add"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowNewForm(false)
                    setNewName("")
                  }}
                >
                  Cancel
                </Button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setShowNewForm(true)}
                className="flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground-secondary transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Create new collection
              </button>
            )}

            <Separator />

            <DialogFooter className="flex-row items-center sm:justify-between">
              <Button
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-600 hover:bg-red-50 gap-1.5"
                onClick={handleRemoveBookmark}
                disabled={isRemoving}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {isRemoving ? "Removing..." : "Remove"}
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? "Saving..." : "Update"}
                </Button>
              </div>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
